type StartupSyncIndicatorDeps = {
   /** Whether the loading state should currently be shown. */
   shouldShow: () => boolean
   /** Pushes the current-fast state (including the loading flag) to the UI. */
   render: () => void
}

/**
 * Drives the startup/foreground cloud-sync loading animation: while the
 * loading state applies, the UI is re-rendered every animation frame so the
 * placeholder ring keeps moving.
 *
 * @param deps - The visibility predicate and render callback.
 * @returns Controls to show or clear the loading state.
 */
export function createStartupSyncIndicator(deps: StartupSyncIndicatorDeps) {
   let frameId: number | null = null

   /**
    * Cancels the pending animation frame.
    */
   const stop = () => {
      if (frameId !== null) {
         window.cancelAnimationFrame(frameId)
         frameId = null
      }
   }
   /**
    * Animation-frame callback that keeps the loading UI in sync.
    */
   const frame = () => {
      if (!deps.shouldShow()) {
         stop()
         return
      }
      deps.render()
      frameId = window.requestAnimationFrame(frame)
   }

   return {
      /**
       * Shows the loading state and starts its animation.
       */
      show: () => {
         if (frameId === null && deps.shouldShow()) {
            frameId = window.requestAnimationFrame(frame)
         }
         deps.render()
      },
      /**
       * Stops the loading state and refreshes the UI.
       */
      clear: () => {
         stop()
         deps.render()
      },
   }
}
